import { type Editor } from '@tiptap/react';
import { useCallback, useState } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { ChevronDownIcon } from '@/components/tiptap-editor/tiptap-icons/chevron-down-icon';

// --- Tiptap UI ---
import { getListShortcutKeys, ListButton, type ListType } from '@/components/tiptap-editor/tiptap-ui/list-button';

import { useListDropdownMenu } from '@/components/tiptap-editor/tiptap-ui/list-dropdown-menu/use-list-dropdown-menu';

// --- UI Primitives ---
import type { ButtonProps } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Card, CardBody } from '@/components/tiptap-editor/tiptap-ui-primitive/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/tiptap-editor/tiptap-ui-primitive/dropdown-menu';
import { ButtonGroup } from '../../tiptap-ui-primitive/button-group';

export interface ListDropdownMenuProps extends Omit<ButtonProps, 'type'> {
    /**
     * The Tiptap editor instance.
     */
    editor?: Editor;
    /**
     * The list types to display in the dropdown.
     */
    types?: ListType[];
    /**
     * Whether the dropdown should be hidden when no list types are available
     * @default false
     */
    hideWhenUnavailable?: boolean;
    /**
     * Callback for when the dropdown opens or closes
     */
    onOpenChange?: (isOpen: boolean) => void;
    /**
     * Whether to render the dropdown menu in a portal
     * @default false
     */
    portal?: boolean;
}

export function ListDropdownMenu({
    editor: providedEditor,
    types = ['bulletList', 'orderedList', 'taskList'],
    hideWhenUnavailable = false,
    onOpenChange,
    portal = true,
    ...props
}: ListDropdownMenuProps) {
    const { editor } = useTiptapEditor(providedEditor);
    const [isOpen, setIsOpen] = useState(false);

    const { filteredLists, canToggle, isActive, isVisible, Icon } = useListDropdownMenu({
        editor,
        types,
        hideWhenUnavailable,
    });

    const handleOnOpenChange = useCallback(
        (open: boolean) => {
            setIsOpen(open);
            onOpenChange?.(open);
        },
        [onOpenChange],
    );

    if (!isVisible) {
        return null;
    }

    return (
        <DropdownMenu
            open={isOpen}
            onOpenChange={handleOnOpenChange}
        >
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    data-style="ghost"
                    data-active-state={isActive ? 'on' : 'off'}
                    role="button"
                    tabIndex={-1}
                    disabled={!canToggle}
                    data-disabled={!canToggle}
                    aria-label="List options"
                    tooltip="List"
                    {...props}
                >
                    <Icon className="tiptap-button-icon" />
                    <ChevronDownIcon className="tiptap-button-dropdown-small" />
                </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align="start"
                portal={portal}
            >
                <Card>
                    <CardBody>
                        <ButtonGroup>
                            {filteredLists.map(option => (
                                <DropdownMenuItem
                                    key={`${option.type}-${option.orderedListType ?? ''}`}
                                    asChild
                                >
                                    <ListButton
                                        editor={editor}
                                        type={option.type}
                                        orderedListType={option.orderedListType}
                                        icon={option.icon}
                                        text={option.label}
                                        shortcutKeys={getListShortcutKeys(option.type, option.orderedListType)}
                                    />
                                </DropdownMenuItem>
                            ))}
                        </ButtonGroup>
                    </CardBody>
                </Card>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default ListDropdownMenu;
